import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Package, ShoppingBag, Users, Euro, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ORDER_LABELS, ORDER_COLORS } from "@/pages/Account";

const CATEGORIES = ["Catene", "Barre", "Ricambi", "Pompe Olio", "Accessori"];
const EMPTY_FORM = { name: "", description: "", price: "", category: "Catene", brand: "", image: "", stock: 10, featured: false, min_qty: 1, max_qty: 0, passo: "", spessore: "", maglie: "" };

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", {
        shipping_cost: parseFloat(settings.shipping_cost) || 0,
        free_shipping_over: parseFloat(settings.free_shipping_over) || 0,
        stripe_enabled: !!settings.stripe_enabled,
        paypal_enabled: !!settings.paypal_enabled,
        cod_enabled: !!settings.cod_enabled,
        shipping_note: settings.shipping_note || "",
      });
      setSettings(data);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user && user.role === "admin") loadAll();
  }, [user]);

  if (user === null) return <div className="max-w-7xl mx-auto px-6 py-24 animate-pulse">Caricamento...</div>;
  if (!user || user.role !== "admin")
    return (
      <div data-testid="admin-denied" className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl uppercase tracking-tight">Accesso riservato</h1>
        <p className="mt-3 text-muted-foreground">Questa area è dedicata all'amministratore del negozio.</p>
        <Link to="/accedi" className="btn-hard mt-6 inline-flex">Accedi</Link>
      </div>
    );

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || "",
      price: p.price,
      category: p.category,
      brand: p.brand || "",
      image: p.image || "",
      stock: p.stock,
      featured: !!p.featured,
      min_qty: p.min_qty || 1,
      max_qty: p.max_qty || 0,
      passo: p.specs?.Passo || "",
      spessore: p.specs?.Spessore || "",
      maglie: p.specs?.Maglie || "",
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const specs = {};
    if (form.passo) specs.Passo = form.passo;
    if (form.spessore) specs.Spessore = form.spessore;
    if (form.maglie) specs.Maglie = form.maglie;
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      category: form.category,
      brand: form.brand,
      image: form.image,
      stock: parseInt(form.stock) || 0,
      featured: form.featured,
      min_qty: parseInt(form.min_qty) || 1,
      max_qty: parseInt(form.max_qty) || 0,
      specs,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/products", payload);
        toast.success("Prodotto creato");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Prodotto eliminato");
      loadAll();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const toggleVisibility = async (p) => {
    try {
      const { data } = await api.post(`/admin/products/${p.id}/toggle-visibility`);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, visible: data.visible } : x)));
      toast.success(data.visible ? "Prodotto ora VISIBILE ai clienti" : "Prodotto ora NASCOSTO ai clienti");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const setOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}`, { status });
      toast.success("Stato ordine aggiornato");
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="overline-tag">Gestione negozio</div>
      <h1 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl md:text-5xl">Pannello admin</h1>

      {stats && (
        <div data-testid="admin-stats" className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Package, label: "Prodotti", value: stats.products },
            { icon: ShoppingBag, label: "Ordini", value: stats.orders },
            { icon: Users, label: "Clienti", value: stats.users },
            { icon: Euro, label: "Fatturato", value: eur(stats.revenue) },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="bg-white border border-[#e5e5e5] p-5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
                <Icon className="w-5 h-5 text-[#ff4500]" strokeWidth={1.5} />
              </div>
              <div className="mt-3 font-display font-semibold text-3xl">{value}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="products" className="mt-12">
        <TabsList className="rounded-none">
          <TabsTrigger data-testid="admin-tab-products" value="products" className="rounded-none uppercase text-xs font-bold tracking-wide">Prodotti</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-orders" value="orders" className="rounded-none uppercase text-xs font-bold tracking-wide">Ordini</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-settings" value="settings" className="rounded-none uppercase text-xs font-bold tracking-wide">Impostazioni</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="mt-6 flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{products.length} prodotti a catalogo</p>
            <button data-testid="admin-new-product-button" onClick={openNew} className="btn-hard !px-5 !py-3 text-xs">
              <Plus className="w-4 h-4" strokeWidth={2} /> Nuovo prodotto
            </button>
          </div>
          <div className="mt-5 bg-white border border-[#e5e5e5] overflow-x-auto">
            <Table data-testid="admin-products-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Prodotto</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Prezzo</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>In evidenza</TableHead>
                  <TableHead>Visibile</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt="" className="w-10 h-10 object-cover border border-[#e5e5e5]" />
                        <div>
                          <div className="font-semibold text-sm">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{p.brand}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{p.category}</TableCell>
                    <TableCell className="font-mono text-sm">{eur(p.price)}</TableCell>
                    <TableCell className={`font-mono text-sm ${p.stock < 2 ? "text-destructive font-bold" : ""}`}>
                      {p.stock}{p.stock < 2 ? " · esaurito" : ""}
                    </TableCell>
                    <TableCell className="text-sm">{p.featured ? "Sì" : "—"}</TableCell>
                    <TableCell>
                      <button
                        data-testid={`admin-visibility-${p.id}`}
                        onClick={() => toggleVisibility(p)}
                        aria-label={p.visible === false ? "Mostra ai clienti" : "Nascondi ai clienti"}
                        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1.5 border-2 transition-colors ${
                          p.visible === false
                            ? "border-[#e5e5e5] text-muted-foreground hover:border-[#0a0a0a]"
                            : "border-[#10b981] text-[#10b981] hover:bg-[#10b981] hover:text-white"
                        }`}
                      >
                        {p.visible === false ? <EyeOff className="w-3.5 h-3.5" strokeWidth={2} /> : <Eye className="w-3.5 h-3.5" strokeWidth={2} />}
                        {p.visible === false ? "Nascosto" : "Visibile"}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:text-[#ff4500] transition-colors" aria-label="Modifica">
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button data-testid={`admin-delete-${p.id}`} onClick={() => removeProduct(p)} className="p-2 hover:text-destructive transition-colors" aria-label="Elimina">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="mt-6 bg-white border border-[#e5e5e5] overflow-x-auto">
            <Table data-testid="admin-orders-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Ordine</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Articoli</TableHead>
                  <TableHead>Totale</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                      Nessun ordine ricevuto ancora.
                    </TableCell>
                  </TableRow>
                )}
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-row-${o.id}`}>
                    <TableCell>
                      <div className="font-mono text-xs">#{o.id.slice(0, 8)}</div>
                      <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("it-IT")}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-semibold">{o.shipping?.nome || "—"}</div>
                      <div className="text-xs text-muted-foreground">{o.user_email}</div>
                      {o.shipping?.indirizzo && (
                        <div data-testid={`order-shipping-${o.id}`} className="mt-2 text-xs bg-[#f4f4f0] border border-[#e5e5e5] p-2 leading-relaxed">
                          📦 {o.shipping.indirizzo}
                          {o.shipping.citta ? `, ${o.shipping.citta}` : ""}
                          {o.shipping.cap ? ` ${o.shipping.cap}` : ""}
                          {o.shipping.telefono ? <><br />📞 {o.shipping.telefono}</> : null}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-semibold">{o.items.reduce((s, i) => s + i.quantity, 0)} pz</div>
                      <div data-testid={`order-items-${o.id}`} className="mt-1 text-xs text-muted-foreground leading-relaxed max-w-[220px]">
                        {o.items.map((i) => (
                          <div key={i.product_id}>• {i.name} × {i.quantity}</div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm font-semibold">{eur(o.total)}</TableCell>
                    <TableCell className="text-xs uppercase">{o.payment_method}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 ${ORDER_COLORS[o.status] || "bg-muted"}`}>
                          {ORDER_LABELS[o.status] || o.status}
                        </span>
                        <Select value={o.status} onValueChange={(v) => setOrderStatus(o.id, v)}>
                          <SelectTrigger data-testid={`admin-order-status-${o.id}`} className="w-36 h-8 rounded-none text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ORDER_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="settings">
          {settings === null ? (
            <div className="py-16 animate-pulse text-muted-foreground">Caricamento impostazioni...</div>
          ) : (
            <form data-testid="settings-form" onSubmit={saveSettings} className="mt-6 max-w-2xl bg-white border border-[#e5e5e5] p-6 sm:p-8 flex flex-col gap-8">
              <div>
                <h3 className="font-display font-medium text-xl uppercase tracking-tight">Spedizione</h3>
                <p className="text-sm text-muted-foreground mt-1">Questi valori si applicano automaticamente al checkout dei clienti.</p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.15em] block mb-2">Costo spedizione+imballo (€)</label>
                    <Input data-testid="settings-shipping-cost" type="number" step="0.01" min="0" value={settings.shipping_cost} onChange={(e) => setSettings({ ...settings, shipping_cost: e.target.value })} className="rounded-none border-2 border-[#0a0a0a]" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.15em] block mb-2">Gratuita sopra (€) — 0 = mai</label>
                    <Input data-testid="settings-free-shipping" type="number" step="0.01" min="0" value={settings.free_shipping_over} onChange={(e) => setSettings({ ...settings, free_shipping_over: e.target.value })} className="rounded-none border-2 border-[#0a0a0a]" />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="text-xs font-bold uppercase tracking-[0.15em] block mb-2">Nota spedizione (mostrata al checkout)</label>
                  <Input data-testid="settings-shipping-note" value={settings.shipping_note} onChange={(e) => setSettings({ ...settings, shipping_note: e.target.value })} placeholder="Es. Spedizione in 24/48 ore con corriere espresso" className="rounded-none border-2 border-[#0a0a0a]" />
                </div>
              </div>

              <div className="border-t border-[#e5e5e5] pt-8">
                <h3 className="font-display font-medium text-xl uppercase tracking-tight">Metodi di pagamento</h3>
                <p className="text-sm text-muted-foreground mt-1">Scegli quali metodi vedono i clienti al checkout.</p>
                <div className="mt-5 flex flex-col gap-4">
                  <label className="flex items-center gap-3 cursor-pointer border-2 border-[#e5e5e5] p-4 hover:border-[#0a0a0a] transition-colors">
                    <Checkbox data-testid="settings-stripe-enabled" checked={!!settings.stripe_enabled} onCheckedChange={(v) => setSettings({ ...settings, stripe_enabled: !!v })} />
                    <span>
                      <span className="block font-semibold text-sm">Carta di credito (Stripe)</span>
                      <span className="block text-xs text-muted-foreground">Visa, Mastercard, Amex — già attivo e funzionante</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer border-2 border-[#e5e5e5] p-4 hover:border-[#0a0a0a] transition-colors">
                    <Checkbox data-testid="settings-paypal-enabled" checked={!!settings.paypal_enabled} onCheckedChange={(v) => setSettings({ ...settings, paypal_enabled: !!v })} />
                    <span>
                      <span className="block font-semibold text-sm">PayPal</span>
                      <span className="block text-xs text-muted-foreground">Richiede le chiavi PayPal configurate — attivalo solo dopo avermele fornite</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer border-2 border-[#e5e5e5] p-4 hover:border-[#0a0a0a] transition-colors">
                    <Checkbox data-testid="settings-cod-enabled" checked={!!settings.cod_enabled} onCheckedChange={(v) => setSettings({ ...settings, cod_enabled: !!v })} />
                    <span>
                      <span className="block font-semibold text-sm">Contrassegno</span>
                      <span className="block text-xs text-muted-foreground">Il cliente paga in contanti al corriere · spedizione sempre applicata (anche sopra la soglia gratuita)</span>
                    </span>
                  </label>
                </div>
              </div>

              <button data-testid="settings-save-button" disabled={savingSettings} className="btn-hard w-full">
                {savingSettings ? "Salvataggio..." : "Salva impostazioni"}
              </button>
            </form>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="product-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">
              {editing ? "Modifica prodotto" : "Nuovo prodotto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="flex flex-col gap-4 mt-2">
            <Input data-testid="product-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome prodotto" className="rounded-none border-2 border-[#0a0a0a]" />
            <Textarea data-testid="product-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrizione" className="rounded-none border-2 border-[#0a0a0a]" />
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="product-form-price" required type="number" step="0.01" min="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Prezzo (€)" className="rounded-none border-2 border-[#0a0a0a]" />
              <Input data-testid="product-form-stock" required type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock" className="rounded-none border-2 border-[#0a0a0a]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-form-category" className="rounded-none border-2 border-[#0a0a0a]">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input data-testid="product-form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Marca (es. Oregon)" className="rounded-none border-2 border-[#0a0a0a]" />
            </div>
            <div className="flex flex-col gap-2">
              <label data-testid="product-form-upload-label" className="btn-outline-hard !py-3 text-xs cursor-pointer text-center">
                {uploading ? "Caricamento foto..." : "Scegli foto dal computer"}
                <input data-testid="product-form-file" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadFile} disabled={uploading} />
              </label>
              <Input data-testid="product-form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="oppure incolla URL immagine (https://...)" className="rounded-none border-2 border-[#0a0a0a]" />
              {form.image && <img data-testid="product-form-preview" src={form.image.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image}` : form.image} alt="Anteprima" className="w-24 h-24 object-cover border border-[#e5e5e5]" />}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Input data-testid="product-form-passo" value={form.passo} onChange={(e) => setForm({ ...form, passo: e.target.value })} placeholder='Passo (es. 3/8")' className="rounded-none border-2 border-[#0a0a0a]" />
              <Input data-testid="product-form-spessore" value={form.spessore} onChange={(e) => setForm({ ...form, spessore: e.target.value })} placeholder="Spessore (es. 1,3 mm)" className="rounded-none border-2 border-[#0a0a0a]" />
              <Input data-testid="product-form-maglie" value={form.maglie} onChange={(e) => setForm({ ...form, maglie: e.target.value })} placeholder="Maglie (es. 52)" className="rounded-none border-2 border-[#0a0a0a]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input data-testid="product-form-min-qty" type="number" min="1" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} placeholder="Qtà minima (pz)" className="rounded-none border-2 border-[#0a0a0a]" />
                <p className="text-xs text-muted-foreground mt-1">Quantità minima acquistabile (es. 5)</p>
              </div>
              <div>
                <Input data-testid="product-form-max-qty" type="number" min="0" value={form.max_qty} onChange={(e) => setForm({ ...form, max_qty: e.target.value })} placeholder="Qtà massima (0 = illimitata)" className="rounded-none border-2 border-[#0a0a0a]" />
                <p className="text-xs text-muted-foreground mt-1">Quantità massima acquistabile (0 = nessun limite)</p>
              </div>
            </div>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <Checkbox data-testid="product-form-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} />
              Mostra in homepage (più venduti)
            </label>
            <button data-testid="product-form-submit" disabled={saving} className="btn-hard w-full">
              {saving ? "Salvataggio..." : editing ? "Salva modifiche" : "Crea prodotto"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
